import type { ErrorCodes } from 'owa-error-codes';
import { type OlkCoreErrorCodes } from 'owa-error-codes';
import type { ErrorType, TraceErrorObject } from 'owa-analytics-types';
import { isTransientResponseCode, isExternalError } from 'owa-errors';
import getDatapointStatus from '../utils/getDatapointStatus';
import { DatapointStatus } from 'owa-analytics-types';
import { tryGetTraceObjectErrorFromApolloError } from 'owa-trace/lib/tryGetTraceObjectErrorFromApolloError';
import getUserErrorCode from './getUserErrorCode';

export interface ErrorCodeAndStatus {
    errorCode: ErrorCodes;
    status: DatapointStatus;
    errorType: ErrorType;
}

export default function getUnhandledErrorCode(
    errorIn: TraceErrorObject,
    status: DatapointStatus,
    errorType?: ErrorType
): ErrorCodeAndStatus {
    const error: TraceErrorObject = tryGetTraceObjectErrorFromApolloError(
        errorIn
    ) as TraceErrorObject;

    let resolvedErrorType = errorType ?? 'general';
    if (error === undefined) {
        return {
            errorCode: '00_0010',
            status,
            errorType: resolvedErrorType,
        };
    }

    if (error?.owaErrorCode) {
        return {
            errorCode: error.owaErrorCode,
            status: error.datapointStatus ?? status,
            errorType: resolvedErrorType,
        };
    }

    if (status == DatapointStatus.UserError || status == DatapointStatus.UserCancelled) {
        return {
            errorCode: error.owaErrorCode ?? '00_0000',
            status,
            errorType: resolvedErrorType,
        };
    }

    let errorCode: ErrorCodes = '00_0000';

    // Check for authentication errors
    if (
        error.httpStatus == 401 ||
        error.httpStatus == 440 ||
        error.fetchErrorType === 'AuthNeeded' ||
        error.responseCode == 'ErrorAuthNeeded'
    ) {
        return {
            errorCode: '00_0011',
            status: DatapointStatus.AuthError,
            errorType: 'auth',
        };
    }

    // Check for access denied errors
    if (error.httpStatus == 403 || error.responseCode == 'ErrorAccessDenied') {
        return {
            errorCode: '00_0006',
            status: DatapointStatus.UserError,
            errorType: 'auth',
        };
    }

    // Check for throttling
    if (error.httpStatus == 429) {
        return {
            errorCode: '00_0003',
            status: DatapointStatus.ServerClientError,
            errorType: 'throttle',
        };
    }

    if (error.responseCode === 'ErrorTooManyObjectsOpened') {
        return {
            errorCode: '00_0009',
            status: DatapointStatus.ServerExpectedError,
            errorType: 'throttle',
        };
    }

    // Check for transient errors
    if (isTransientResponseCode(error)) {
        return {
            errorCode: '00_0002',
            status: DatapointStatus.ServerExpectedError,
            errorType: 'transient',
        };
    }

    // Check for user errors
    const userErrorCode = getUserErrorCode(error);
    if (userErrorCode) {
        return {
            errorCode: userErrorCode,
            status: DatapointStatus.UserError,
            errorType: 'configuration',
        };
    }

    if (error.owaServiceResultType == 'ClientError') {
        return {
            errorCode: '00_0007',
            status: DatapointStatus.ServerClientError,
            errorType: 'general',
        };
    }

    // External errors are errors that originate from sources outside of our application control.
    if (isExternalError(error.message, error)) {
        return {
            errorCode: '00_0012',
            status: DatapointStatus.UserError,
            errorType: 'external',
        };
    }

    // Fall back to generic error analysis
    const statusAndType = getDatapointStatus(error.message, error);
    // Only override status if getDatapointStatus provided a more specific categorization
    if (statusAndType.status !== DatapointStatus.ClientError) {
        status = statusAndType.status;
    }

    if (statusAndType?.type) {
        resolvedErrorType = statusAndType.type;

        // Map specific error types to error codes
        const errorTypeMap: Record<string, ErrorCodes> = {
            network: '00_0001',
            memory: '00_0005',
            auth: '00_0006',
            throttle: '00_0003',
            transient: '00_0002',
        };

        errorCode = errorTypeMap[statusAndType.type] ?? errorCode;
    }

    return { errorCode, status, errorType: resolvedErrorType };
}
